import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import TableSortLabel from '@mui/material/TableSortLabel';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import { useData } from './DataProvider';

export default function StoryItems({storyitemsUrl, buttonProps = {} }) {
  const [open, setOpen] = useState(false);
  const { storyitems, setStoryItems } = useData();
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [orderBy, setOrderBy] = useState('Thumbprint');
  const [order, setOrder] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');

  // Helper function to highlight matching text
  const highlightText = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <Box 
          key={index} 
          component="span" 
          sx={{ 
            backgroundColor: '#ffeb3b', 
            color: '#000',
            fontWeight: 'bold',
            padding: '0 2px',
            borderRadius: '2px'
          }}
        >
          {part}
        </Box>
      ) : part
    );
  };

  useEffect(() => {
    if (!storyitemsUrl) {
      setError('No collect.json URL provided.');
      return;
    }
    fetch(storyitemsUrl)
      .then(res => {
        if (!res.ok) throw new Error('Failed to load collect.json from blob storage');
        return res.json();
      })
      .then(data => {
        const arr = data;
        const justprocess = arr.filter(si => si.ItemType === "Process");
        setStoryItems(justprocess);
        if (justprocess.length > 0) setSelected(justprocess[0]);
        setError(null);
      })
      .catch(e => setError(e.message));
  }, [storyitemsUrl, setStoryItems]);

  const handleSort = (column) => {
    if (orderBy === column) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(column);
      setOrder('asc');
    }
  };

  const sortedStoryItems = React.useMemo(() => {
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    
    // First filter by search term
    const filteredStoryItems = storyitems.filter(si => (
      (si.ItemName && si.ItemName.toLowerCase().includes(searchTerm.toLowerCase()))
        || (si.ParentUProcessID && `${si.ParentUProcessID}`.includes(searchTerm.toLowerCase()))
        || (si.ProcessName && si.ProcessName.toLowerCase().includes(searchTerm.toLowerCase()))
        || (si.Actions && si.Actions.some(a => (
                                                  (a.Details && a.Details.toLowerCase().includes(searchTerm.toLowerCase()))
                                                  || (a.UPid && `${a.UPid}`.includes(searchTerm.toLowerCase()))
                                                  || (a.ProcessName && a.ProcessName.toLowerCase().includes(searchTerm.toLowerCase()))
                                                  || (a.Action && a.Action.toLowerCase().includes(searchTerm.toLowerCase()))
                                                )
                                              ))
      )
    );
    
    return [...filteredStoryItems].sort((a, b) => {
      let aValue = a[orderBy] ?? '';
      let bValue = b[orderBy] ?? '';
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }
      return order === 'asc' ? collator.compare(aValue, bValue) : collator.compare(bValue, aValue);
    });
  }, [storyitems, orderBy, order, searchTerm]);

  // Button to open dialog
  return (
    <React.Fragment>
      <Button
        size="small"
        startIcon={<InfoOutlinedIcon sx={{ fontSize: 18 }} />}
        onClick={() => setOpen(true)}
        sx={{ py: 0.5, minHeight: 28 }}
        {...buttonProps}
      >
        Process Info
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { width: '90vw', height: '90vh', maxWidth: 'none', maxHeight: 'none' } }}
      >
        <DialogContent sx={{ p: 3, height: 'calc(90vh - 70px)', boxSizing: 'border-box' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Process Info</Typography>
          <Divider sx={{ mb: 2 }} />
          <Box p={0} display="flex" flexDirection="row" gap={4} height="calc(100% - 80px)">
            <Box flex={2} minHeight={0} display="flex" flexDirection="column">
              <Box mb={2} display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Total certs: {storyitems.length} {searchTerm && `(${sortedStoryItems.length} matching)`}
                </Typography>
                <TextField
                  size="small"
                  placeholder="Search certs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 250 }}
                />
              </Box>
              <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sortDirection={orderBy === 'ItemName' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'ItemName'}
                          direction={orderBy === 'ItemName' ? order : 'asc'}
                          onClick={() => handleSort('ItemName')}
                        >
                          UProcessID
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={orderBy === 'ProcessName' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'ProcessName'}
                          direction={orderBy === 'ProcessName' ? order : 'asc'}
                          onClick={() => handleSort('ProcessName')}
                        >
                          ProcessName
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={orderBy === 'ParentUProcessID' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'ParentUProcessID'}
                          direction={orderBy === 'ParentUProcessID' ? order : 'asc'}
                          onClick={() => handleSort('ParentUProcessID')}
                        >
                          ParentUProcessID
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedStoryItems.map((proc, idx) => (
                      <TableRow
                        key={proc.ItemName || idx}
                        hover
                        selected={selected && selected.ItemName === proc.ItemName}
                        onClick={() => setSelected(proc)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell sx={{ maxWidth: 320, wordBreak: 'break-all' }}>
                          {highlightText(proc.ItemName, searchTerm)}
                        </TableCell>
                        <TableCell align="left">{proc.ProcessName ? proc.ProcessName : ''}</TableCell>
                        <TableCell align="right">{proc.ParentUProcessID ? proc.ParentUProcessID : ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            <Box flex={1} minWidth={320} minHeight={0} display="flex" flexDirection="column" height="100%">
              {selected ? (
                <Paper sx={{ p: 2, flex: 1, maxHeight: '100%', overflow: 'auto', minHeight: 0 }}>
                  <List dense>
                    {Object.entries(selected).map(([key, value]) => (
                      <ListItem key={key} alignItems="flex-start" disablePadding>
                        <ListItemText
                          primary={<Typography variant="body2" sx={{ fontWeight: 500 }}>{key}</Typography>}
                          secondary={
                                      Array.isArray(value) ? (
                                        value.length > 0 ? (
                                          value.map((v, i) =>
                                            typeof v === 'object' && v !== null ? (
                                              <span key={i}>[{v.Action}] {String(v.ActionTime)} {v.Details || ''}<br /></span>
                                            ) : (
                                              <span key={i}>{String(v)}<br /></span>
                                            )
                                          )
                                        ) : (
                                          <em>None</em>
                                        )
                                      ) : typeof value === 'boolean' ? (
                                        value ? 'true' : 'false'
                                      ) : (
                                        String(value)
                                      )
                                    }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              ) : (
                <Typography color="text.secondary">Select a process to see details</Typography>
              )}
            </Box>
          </Box>
          {error && <Typography color="error" sx={{ p: 2 }}>{error}</Typography>}
          {!storyitems.length && !error && <Typography sx={{ p: 2 }}>Loading copycert info...</Typography>}
        </DialogContent>
        <Divider sx={{ width: '100%' }} />
        <Box sx={{ mt: 2, mb: 3, px: 3, textAlign: 'right' }}>
          <Button onClick={() => setOpen(false)} variant="contained">Close</Button>
        </Box>
      </Dialog>
    </React.Fragment>
  );
}
